import { Link } from 'react-router-dom';
import { useState, memo } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateWebPageSchema } from '../../utils/seo';

// Composant image optimisé avec lazy loading
const OptimizedImage = memo(({ src, alt, className }: { src: string; alt: string; className?: string }) => (
  <img 
    src={src}
    alt={alt}
    className={className}
    loading="lazy"
    decoding="async"
  />
));

// Composant pour l'image hero (prioritaire)
const HeroImage = memo(({ src, alt, className }: { src: string; alt: string; className?: string }) => (
  <img 
    src={src}
    alt={alt}
    className={className}
    fetchPriority="high"
    decoding="async"
  />
));

function Home() {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  // SEO Configuration
  useSEO({
    title: 'PRT Propreté - Lavage Auto à Creil',
    description: 'PRT Proprete - Préparateur auto professionnel à Creil (60). Service de lavage automobile premium avec Pack Premium 60€ et Pack Luxe 80€. Déplacement gratuit dans un rayon de 35kms. Nettoyage intérieur et extérieur de qualité professionnelle.',
    keywords: 'lavage auto Creil, préparateur auto, nettoyage voiture, detailing auto, lavage intérieur, lavage extérieur, Creil 60',
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        generateWebPageSchema('PRT Propreté - Lavage Auto à Creil', 'Service de lavage automobile premium à Creil')
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <HeroImage 
            src="https://public.readdy.ai/ai/img_res/b0798bfb8daff7ceb1cdaaf59881ec26.jpg"
            alt="Car Wash Background"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a3e]/85 via-[#2d2d6e]/80 to-[#00B4D8]/70"></div>
        </div>

        {/* Animated Bubbles - Simplifié */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-20 sm:w-32 h-20 sm:h-32 bg-[#00B4D8]/20 rounded-full blur-2xl"></div>
          <div className="absolute top-40 right-10 sm:right-20 w-32 sm:w-48 h-32 sm:h-48 bg-[#E63946]/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-32 left-1/4 w-24 sm:w-40 h-24 sm:h-40 bg-[#00B4D8]/20 rounded-full blur-2xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center w-full pt-20 sm:pt-24">
          <div className="transform hover:scale-105 transition-transform duration-500">
            <img 
              src="https://static.readdy.ai/image/a17d3ae5e640a18a289681692364ae47/58da3f7e4785850c57041e6c71714363.png" 
              alt="PRT Proprete Logo" 
              className="w-48 sm:w-64 md:w-80 h-auto mx-auto mb-6 sm:mb-8 md:mb-12 drop-shadow-2xl"
              fetchPriority="high"
            />
          </div>
          
          <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-8xl font-black text-white mb-4 sm:mb-6 leading-tight">
            L'Excellence du<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00B4D8] to-[#E63946]">Lavage Auto</span>
          </h1>
          
          <p className="text-base sm:text-lg md:text-xl lg:text-3xl text-white mb-8 sm:mb-10 md:mb-12 max-w-4xl mx-auto leading-relaxed font-medium px-2 drop-shadow-lg" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.9), 0 4px 16px rgba(0,0,0,0.7)' }}>
            Redonnez à votre véhicule l'éclat du neuf avec nos prestations premium. 
            Un service professionnel qui fait toute la différence.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center px-4">
            <Link 
              to="/prestations" 
              className="whitespace-nowrap w-full sm:w-auto px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-[#E63946] to-[#FF6B6B] text-white rounded-full text-base sm:text-lg font-bold hover:shadow-2xl hover:shadow-[#E63946]/50 transition-all duration-300 transform hover:scale-110 cursor-pointer shadow-xl"
            >
              <i className="ri-star-line mr-2"></i>
              Découvrir nos offres
            </Link>
            <Link 
              to="/contact" 
              className="whitespace-nowrap w-full sm:w-auto px-8 sm:px-12 py-4 sm:py-5 bg-white/20 backdrop-blur-md text-white border-2 border-white rounded-full text-base sm:text-lg font-bold hover:bg-white/30 transition-all duration-300 transform hover:scale-110 cursor-pointer shadow-xl"
            >
              <i className="ri-calendar-check-line mr-2"></i>
              Réserver maintenant
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 sm:gap-8 max-w-3xl mx-auto mt-12 sm:mt-16 md:mt-20 px-2">
            <div className="text-center bg-black/30 backdrop-blur-sm rounded-2xl py-4 px-2">
              <div className="text-2xl sm:text-4xl md:text-5xl font-black text-[#00B4D8] mb-1 sm:mb-2 drop-shadow-lg" style={{ textShadow: '0 2px 8px rgba(0,180,216,0.5)' }}>100%</div>
              <div className="text-white font-bold text-xs sm:text-sm md:text-base drop-shadow-md" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>Satisfaction</div>
            </div>
            <div className="text-center bg-black/30 backdrop-blur-sm rounded-2xl py-4 px-2">
              <div className="text-2xl sm:text-4xl md:text-5xl font-black text-[#E63946] mb-1 sm:mb-2 drop-shadow-lg" style={{ textShadow: '0 2px 8px rgba(230,57,70,0.5)' }}>35km</div>
              <div className="text-white font-bold text-xs sm:text-sm md:text-base drop-shadow-md" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>Gratuit</div>
            </div>
            <div className="text-center bg-black/30 backdrop-blur-sm rounded-2xl py-4 px-2">
              <div className="text-2xl sm:text-4xl md:text-5xl font-black text-[#00B4D8] mb-1 sm:mb-2 drop-shadow-lg" style={{ textShadow: '0 2px 8px rgba(0,180,216,0.5)' }}>Pro</div>
              <div className="text-white font-bold text-xs sm:text-sm md:text-base drop-shadow-md" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>Produits</div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-6 sm:bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white text-2xl sm:text-3xl"></i>
        </div>
      </section>

      {/* Video Section - Chargement différé */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gradient-to-b from-[#1a1a3e] to-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <span className="text-[#00B4D8] font-bold text-sm sm:text-lg mb-2 sm:mb-4 block">NOTRE TRAVAIL EN ACTION</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4">
              Découvrez Notre <span className="text-[#E63946]">Savoir-Faire</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-300 max-w-2xl mx-auto">
              Regardez notre équipe en action et découvrez la qualité de nos prestations
            </p>
          </div>

          <div className="flex justify-center">
            <div className="relative group w-full max-w-md">
              <div className="relative bg-gradient-to-br from-[#00B4D8]/20 to-[#E63946]/20 rounded-2xl sm:rounded-3xl p-1 shadow-2xl">
                <div className="relative bg-gray-900 rounded-xl sm:rounded-2xl overflow-hidden">
                  <div className="aspect-[9/16] w-full">
                    <iframe
                      src="https://www.instagram.com/reel/DDXfe4fNxuQ/embed"
                      className="w-full h-full border-0"
                      allowFullScreen
                      scrolling="no"
                      title="PRT Proprete - Vidéo de démonstration"
                      loading="lazy"
                    ></iframe>
                  </div>
                  
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 sm:p-6">
                    <button
                      onClick={() => setIsVideoModalOpen(true)}
                      className="whitespace-nowrap w-full px-6 py-3 bg-gradient-to-r from-[#E63946] to-[#FF6B6B] text-white rounded-full font-bold hover:shadow-lg hover:shadow-[#E63946]/50 transition-all duration-300 transform hover:scale-105 cursor-pointer flex items-center justify-center gap-2 text-sm sm:text-base"
                    >
                      <i className="ri-fullscreen-line text-lg"></i>
                      Agrandir la vidéo
                    </button>
                  </div>
                </div>
              </div>

              <div className="absolute -top-4 -right-4 w-20 h-20 bg-[#00B4D8]/30 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-[#E63946]/30 rounded-full blur-2xl"></div>
            </div>
          </div>

          <div className="text-center mt-8">
            <a
              href="https://www.instagram.com/prt.proprete/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-[#E63946] transition-colors duration-300 cursor-pointer"
            >
              <i className="ri-instagram-line text-xl"></i>
              <span className="font-medium">Suivez-nous sur Instagram pour plus de vidéos</span>
            </a>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {isVideoModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
          onClick={() => setIsVideoModalOpen(false)}
        >
          <div 
            className="relative w-full max-w-2xl max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsVideoModalOpen(false)}
              className="absolute -top-12 right-0 text-white hover:text-[#E63946] transition-colors duration-300 cursor-pointer flex items-center gap-2"
            >
              <span className="font-medium">Fermer</span>
              <i className="ri-close-line text-3xl"></i>
            </button>

            <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
              <div className="aspect-[9/16] w-full">
                <iframe
                  src="https://www.instagram.com/reel/DDXfe4fNxuQ/embed"
                  className="w-full h-full border-0"
                  allowFullScreen
                  scrolling="no"
                  title="PRT Proprete - Vidéo de démonstration"
                ></iframe>
              </div>
            </div>

            <div className="mt-4 text-center">
              <a
                href="https://www.instagram.com/reel/DDXfe4fNxuQ/?igsh=aXdvanNsajR6OHYz"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#E63946] to-[#FF6B6B] text-white rounded-full font-bold hover:shadow-lg transition-all duration-300 cursor-pointer"
              >
                <i className="ri-instagram-line text-xl"></i>
                Voir sur Instagram
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Services Preview */}
      <section className="py-12 sm:py-16 md:py-24 px-4 relative bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16 md:mb-20">
            <span className="text-[#E63946] font-bold text-sm sm:text-lg mb-2 sm:mb-4 block">NOS FORMULES</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-4 sm:mb-6">
              Choisissez Votre <span className="text-[#00B4D8]">Prestation</span>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-2">
              Des services adaptés à tous vos besoins pour un résultat impeccable
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 md:gap-10 max-w-6xl mx-auto">
            {/* Pack Premium */}
            <div className="group bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-xl hover:shadow-2xl transition-all duration-500 border-2 border-gray-100 hover:border-[#00B4D8] transform hover:-translate-y-2 cursor-pointer">
              <div className="text-center mb-6 sm:mb-8">
                <div className="inline-block p-3 sm:p-4 bg-gradient-to-br from-[#00B4D8]/10 to-[#00B4D8]/5 rounded-xl sm:rounded-2xl mb-3 sm:mb-4">
                  <i className="ri-car-washing-line text-4xl sm:text-5xl md:text-6xl text-[#00B4D8]"></i>
                </div>
                <h3 className="text-2xl sm:text-3xl font-black text-gray-900 mb-3 sm:mb-4">Pack Premium</h3>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-4xl sm:text-5xl md:text-6xl font-black text-[#00B4D8]">60€</span>
                </div>
              </div>
              
              <ul className="space-y-3 sm:space-y-4 text-gray-700 mb-6 sm:mb-8">
                {['Nettoyage moquette en profondeur', 'Nettoyage des tapis en profondeur', 'Nettoyage des plastiques', 'Shampouinage des sièges', 'Nettoyage des vitres', 'Nettoyage entourage des portes'].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-[#00B4D8]/20 rounded-full flex items-center justify-center mr-2 sm:mr-3 mt-0.5 flex-shrink-0">
                      <i className="ri-check-line text-[#00B4D8] text-xs sm:text-sm"></i>
                    </div>
                    <span className="font-medium text-sm sm:text-base">{item}</span>
                  </li>
                ))}
              </ul>
              
              <Link 
                to="/contact" 
                className="whitespace-nowrap w-full block text-center px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-[#00B4D8] to-[#0096C7] text-white rounded-full font-bold hover:shadow-lg hover:shadow-[#00B4D8]/50 transition-all duration-300 transform group-hover:scale-105 cursor-pointer text-sm sm:text-base"
              >
                Choisir cette formule
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>

            {/* Pack Luxe */}
            <div className="group bg-gradient-to-br from-[#E63946] to-[#FF6B6B] rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:-translate-y-2 cursor-pointer relative overflow-hidden">
              <div className="absolute top-4 right-4 sm:top-6 sm:right-6 bg-white text-[#E63946] px-3 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-black shadow-lg">
                ⭐ RECOMMANDÉ
              </div>

              <div className="absolute -top-10 -right-10 w-32 sm:w-40 h-32 sm:h-40 bg-white/10 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-10 -left-10 w-32 sm:w-40 h-32 sm:h-40 bg-white/10 rounded-full blur-2xl"></div>
              
              <div className="text-center mb-6 sm:mb-8 relative z-10 pt-6 sm:pt-0">
                <div className="inline-block p-3 sm:p-4 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl mb-3 sm:mb-4">
                  <i className="ri-vip-crown-line text-4xl sm:text-5xl md:text-6xl text-white"></i>
                </div>
                <h3 className="text-2xl sm:text-3xl font-black text-white mb-3 sm:mb-4">Pack Luxe</h3>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-4xl sm:text-5xl md:text-6xl font-black text-white">80€</span>
                </div>
              </div>
              
              <ul className="space-y-3 sm:space-y-4 text-white mb-6 sm:mb-8 relative z-10">
                {['Nettoyage moquette en profondeur', 'Nettoyage des tapis en profondeur', 'Nettoyage des plastiques', 'Shampouinage des sièges', 'Nettoyage des vitres', 'Nettoyage entourage des portes'].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white/30 rounded-full flex items-center justify-center mr-2 sm:mr-3 mt-0.5 flex-shrink-0">
                      <i className="ri-check-line text-white text-xs sm:text-sm"></i>
                    </div>
                    <span className="font-semibold text-sm sm:text-base">{item}</span>
                  </li>
                ))}
                <li className="flex items-start">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white rounded-full flex items-center justify-center mr-2 sm:mr-3 mt-0.5 flex-shrink-0">
                    <i className="ri-check-line text-[#E63946] text-xs sm:text-sm font-bold"></i>
                  </div>
                  <span className="font-black text-sm sm:text-base md:text-lg">+ Nettoyage extérieur complet</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white rounded-full flex items-center justify-center mr-2 sm:mr-3 mt-0.5 flex-shrink-0">
                    <i className="ri-check-line text-[#E63946] text-xs sm:text-sm font-bold"></i>
                  </div>
                  <span className="font-black text-sm sm:text-base md:text-lg">+ Nettoyage jantes</span>
                </li>
              </ul>
              
              <Link 
                to="/contact" 
                className="whitespace-nowrap relative z-10 w-full block text-center px-6 sm:px-8 py-3 sm:py-4 bg-white text-[#E63946] rounded-full font-black hover:shadow-2xl transition-all duration-300 transform group-hover:scale-105 cursor-pointer text-sm sm:text-base"
              >
                Choisir cette formule
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Before/After Gallery */}
      <section className="py-12 sm:py-16 md:py-24 px-4 bg-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-48 sm:w-96 h-48 sm:h-96 bg-[#00B4D8] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-48 sm:w-96 h-48 sm:h-96 bg-[#E63946] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-10 sm:mb-16">
            <span className="text-[#00B4D8] font-bold text-sm sm:text-lg mb-2 sm:mb-4 block">RÉSULTATS</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4 sm:mb-6">
              Avant / Après
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-300 px-2">
              Découvrez la transformation de vos véhicules
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 max-w-4xl mx-auto">
            <Link to="/galerie" className="group relative overflow-hidden rounded-xl sm:rounded-2xl shadow-2xl transform hover:scale-105 transition-all duration-500 cursor-pointer block">
              <div className="w-full h-64 sm:h-72 md:h-80">
                <OptimizedImage 
                  src="https://static.readdy.ai/image/a17d3ae5e640a18a289681692364ae47/57756a9ee8d47789457bd3e7801847cc.jpeg"
                  alt="Avant/Après Pack Premium"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-4 sm:p-6">
                <div>
                  <h3 className="text-white font-bold text-lg sm:text-xl mb-1 sm:mb-2">Pack Premium</h3>
                  <p className="text-gray-300 text-xs sm:text-sm">Nettoyage intérieur complet</p>
                  <span className="inline-flex items-center gap-1 text-[#00B4D8] text-xs sm:text-sm font-semibold mt-2 group-hover:gap-2 transition-all">
                    Voir plus <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </Link>

            <Link to="/galerie" className="group relative overflow-hidden rounded-xl sm:rounded-2xl shadow-2xl transform hover:scale-105 transition-all duration-500 cursor-pointer block">
              <div className="w-full h-64 sm:h-72 md:h-80">
                <OptimizedImage 
                  src="https://static.readdy.ai/image/a17d3ae5e640a18a289681692364ae47/acfc91620f3fd7c53e7276106bedcde6.jpeg"
                  alt="Avant/Après Pack Luxe"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-4 sm:p-6">
                <div>
                  <h3 className="text-white font-bold text-lg sm:text-xl mb-1 sm:mb-2">Pack Luxe</h3>
                  <p className="text-gray-300 text-xs sm:text-sm">Intérieur + Extérieur complet</p>
                  <span className="inline-flex items-center gap-1 text-[#00B4D8] text-xs sm:text-sm font-semibold mt-2 group-hover:gap-2 transition-all">
                    Voir plus <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </Link>
          </div>

          <div className="text-center mt-8 sm:mt-12">
            <Link 
              to="/galerie"
              className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#00B4D8] to-[#0096C7] text-white rounded-full font-bold hover:shadow-lg hover:shadow-[#00B4D8]/50 transition-all duration-300 transform hover:scale-105 cursor-pointer"
            >
              <i className="ri-gallery-line text-xl"></i>
              Voir toute la galerie
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 sm:py-16 md:py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16 md:mb-20">
            <span className="text-[#E63946] font-bold text-sm sm:text-lg mb-2 sm:mb-4 block">NOTRE ENGAGEMENT</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-4 sm:mb-6">
              Pourquoi <span className="text-[#00B4D8]">Nous Choisir ?</span>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-2">
              Un service professionnel qui fait toute la différence
            </p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            {[
              { icon: 'ri-star-line', title: 'Qualité Premium', desc: 'Des prestations de haute qualité pour un résultat impeccable', color: 'from-[#00B4D8] to-[#0096C7]' },
              { icon: 'ri-flask-line', title: 'Produits Pro', desc: 'Utilisation exclusive de produits professionnels haut de gamme', color: 'from-[#E63946] to-[#FF6B6B]' },
              { icon: 'ri-shield-check-line', title: 'Respect Total', desc: 'Un soin particulier et respectueux apporté à votre véhicule', color: 'from-[#00B4D8] to-[#0096C7]' },
              { icon: 'ri-heart-line', title: '100% Satisfait', desc: 'Votre satisfaction est notre priorité absolue', color: 'from-[#E63946] to-[#FF6B6B]' }
            ].map((item, index) => (
              <div key={index} className="group text-center p-4 sm:p-6 md:p-8 bg-gradient-to-br from-gray-50 to-white rounded-xl sm:rounded-2xl hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer border border-gray-100">
                <div className={`w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-gradient-to-br ${item.color} rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 transform group-hover:rotate-6 transition-transform duration-300 shadow-lg`}>
                  <i className={`${item.icon} text-3xl sm:text-4xl md:text-5xl text-white`}></i>
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-black text-gray-900 mb-2 sm:mb-4">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed text-xs sm:text-sm md:text-base">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-24 px-4 bg-gradient-to-br from-[#1a1a3e] via-[#2d2d6e] to-[#00B4D8] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-32 sm:w-64 h-32 sm:h-64 bg-[#E63946] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-48 sm:w-96 h-48 sm:h-96 bg-[#00B4D8] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-5xl mx-auto text-center relative z-10 px-2">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4 sm:mb-6 md:mb-8 leading-tight">
            Prêt à Transformer<br />Votre Véhicule ?
          </h2>
          <p className="text-lg sm:text-xl md:text-2xl text-gray-200 mb-8 sm:mb-10 md:mb-12 leading-relaxed">
            Réservez dès maintenant votre prestation et découvrez la différence
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center">
            <Link 
              to="/contact" 
              className="whitespace-nowrap w-full sm:w-auto px-8 sm:px-12 py-4 sm:py-6 bg-gradient-to-r from-[#E63946] to-[#FF6B6B] text-white rounded-full text-base sm:text-lg md:text-xl font-black hover:shadow-2xl hover:shadow-[#E63946]/50 transition-all duration-300 transform hover:scale-110 cursor-pointer"
            >
              <i className="ri-calendar-check-line mr-2"></i>
              Réserver maintenant
            </Link>
            <a 
              href="tel:0776969414" 
              className="whitespace-nowrap w-full sm:w-auto px-8 sm:px-12 py-4 sm:py-6 bg-white text-[#1a1a3e] rounded-full text-base sm:text-lg md:text-xl font-black hover:shadow-2xl transition-all duration-300 transform hover:scale-110 cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              07.76.96.94.14
            </a>
          </div>

          <div className="mt-10 sm:mt-12 md:mt-16 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 text-white">
            <i className="ri-map-pin-line text-xl sm:text-2xl text-[#00B4D8]"></i>
            <span className="text-sm sm:text-base md:text-lg font-semibold text-center">Déplacement gratuit dans un rayon de 35km autour de Creil</span>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default memo(Home);
